/**
 * TimezoneService — the ONLY place in the admin app that converts between a wall
 * clock in an IANA zone and a UTC instant (luxon).
 *
 * Conventions (mirror the backend `src/common/timezone/tz-core.ts`):
 *  - local date  = 'YYYY-MM-DD'   local time = 'HH:mm'   instant = ISO string with 'Z'
 *  - the backend receives `{ date, start_time, end_time, timezone }` and returns
 *    `start_at` / `end_at` UTC instants; this module renders those in the zone the
 *    admin selected (see `useDisplayTimezone`).
 *  - Never use `new Date(y, m, d, h, i)`, `getHours()`, `toLocale*` or
 *    `toISOString().split('T')[0]` in feature code — call these helpers.
 */
import { DateTime, IANAZone } from 'luxon';

export const DEFAULT_BUSINESS_TIMEZONE = 'Asia/Manila';

export const LOCAL_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const LOCAL_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Browser/device zone (spec §5 — the selector's default). */
export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** True for a real IANA zone (`Asia/Manila`, `UTC`); rejects `EST`, `+08:00`, `UTC+1`. */
export function validateTimezone(tz) {
  if (typeof tz !== 'string') return false;
  const v = tz.trim();
  if (!v) return false;
  if (v !== 'UTC' && !v.includes('/')) return false;
  if (/[+-]\d/.test(v)) return false;
  return IANAZone.isValidZone(v);
}

export function normalizeTimezone(tz) {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz }).resolvedOptions().timeZone;
  } catch {
    return tz;
  }
}

function assertZone(tz) {
  if (!validateTimezone(tz)) throw new RangeError(`Invalid IANA timezone: ${tz}`);
}

/** Offset in minutes east of UTC at `at` (default now). */
export function getTimezoneOffset(tz, at = new Date()) {
  assertZone(tz);
  return DateTime.fromJSDate(at, { zone: 'utc' }).setZone(tz).offset;
}

/** `UTC+08:00` style label. */
export function getOffsetLabel(tz, at = new Date()) {
  const m = getTimezoneOffset(tz, at);
  const sign = m < 0 ? '-' : '+';
  const abs = Math.abs(m);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

/** Short zone abbreviation for the current instant, e.g. `PST`, `GMT+8`. */
export function getZoneAbbreviation(tz, at = new Date()) {
  assertZone(tz);
  return DateTime.fromJSDate(at, { zone: 'utc' }).setZone(tz).toFormat('ZZZZ');
}

/**
 * `'2026-09-20'` + `'07:00'` in `'Asia/Dubai'` -> `'2026-09-20T03:00:00.000Z'`.
 * DST policy (same as the backend): a non-existent local time moves forward;
 * an ambiguous one resolves to the earlier instant.
 */
export function localToUtc(date, time, tz) {
  assertZone(tz);
  if (!LOCAL_DATE_RE.test(date)) throw new RangeError(`Invalid local date: ${date}`);
  if (!LOCAL_TIME_RE.test(time)) throw new RangeError(`Invalid local time: ${time}`);
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  let dt = DateTime.fromObject({ year: y, month: mo, day: d, hour: h, minute: mi }, { zone: tz });
  if (!dt.isValid) throw new RangeError(`Invalid local date/time ${date} ${time}`);
  const earlier = dt.minus({ hours: 1 });
  if (earlier.hour === h && earlier.minute === mi && earlier.day === d) dt = earlier;
  return dt.toUTC().toISO();
}

/** UTC instant (ISO/Date) -> `{ date, time, weekday, hour, minute, offsetMinutes }` in `tz`. */
export function utcToLocal(instant, tz) {
  assertZone(tz);
  const dt = toDateTime(instant).setZone(tz);
  if (!dt.isValid) throw new RangeError(`Invalid instant: ${instant}`);
  return {
    date: dt.toISODate(),
    time: dt.toFormat('HH:mm'),
    weekday: dt.weekday % 7, // 0=Sun..6=Sat
    hour: dt.hour,
    minute: dt.minute,
    offsetMinutes: dt.offset,
    iso: dt.toISO(),
  };
}

/** luxon-format an instant in `tz`. */
export function formatUtcInTimezone(instant, tz, format = 'yyyy-MM-dd HH:mm') {
  assertZone(tz);
  const dt = toDateTime(instant).setZone(tz);
  return dt.isValid ? dt.toFormat(format) : 'Invalid time value';
}

export function getStartOfDayInUtc(date, tz) {
  return localToUtc(date, '00:00', tz);
}

/** Exclusive: the next local midnight. */
export function getEndOfDayInUtc(date, tz) {
  return localToUtc(addLocalDays(date, 1), '00:00', tz);
}

/** Local calendar date of an instant in `tz`. */
export function localDateOf(instant, tz) {
  return utcToLocal(instant, tz).date;
}

export function todayLocalDate(tz, now = new Date()) {
  return localDateOf(now, tz);
}

export function addLocalDays(date, days) {
  if (!LOCAL_DATE_RE.test(date)) throw new RangeError(`Invalid local date: ${date}`);
  return DateTime.fromISO(date, { zone: 'utc' }).plus({ days }).toISODate();
}

/**
 * Parse anything the API or a picker hands us into a luxon DateTime in UTC.
 * A string with no zone designator is treated as UTC (the backend's legacy
 * `1970-01-01T07:00:00.000Z` / `2026-09-20T00:00:00.000Z` values are UTC-labelled).
 */
export function toDateTime(value) {
  if (value instanceof Date) return DateTime.fromJSDate(value, { zone: 'utc' });
  if (value && typeof value === 'object' && typeof value.toDate === 'function') {
    // dayjs / moment
    return DateTime.fromJSDate(value.toDate(), { zone: 'utc' });
  }
  if (typeof value === 'number') return DateTime.fromMillis(value, { zone: 'utc' });
  if (typeof value === 'string') {
    const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
    return DateTime.fromISO(value, { zone: hasZone ? undefined : 'utc', setZone: false }).toUTC();
  }
  return DateTime.invalid('unsupported input');
}

/** True for a bare `HH:mm` wall-clock string (a rule value that must not be converted). */
export function isWallClockTime(value) {
  return typeof value === 'string' && /^\d{1,2}:\d{2}$/.test(value);
}

/**
 * True for a legacy `@db.Time(0)` value (`1970-01-01THH:mm:00.000Z`): a wall clock
 * labelled UTC, not an instant. Rendered as-is, never converted.
 */
export function isLegacyTimeValue(value) {
  const dt = toDateTime(value);
  return dt.isValid && dt.year < 1972;
}

// ---------------------------------------------------------------------------
// Selector catalogue: country -> IANA zones (curated; every zone validated at runtime)
// ---------------------------------------------------------------------------

const RAW_CATALOG = [
  ['Philippines', ['Asia/Manila']],
  ['United Arab Emirates', ['Asia/Dubai']],
  ['Germany', ['Europe/Berlin']],
  ['United Kingdom', ['Europe/London']],
  ['India', ['Asia/Kolkata']],
  ['United States', ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu']],
  ['Australia', ['Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Perth', 'Australia/Darwin', 'Australia/Hobart']],
  ['Singapore', ['Asia/Singapore']],
  ['Japan', ['Asia/Tokyo']],
  ['South Korea', ['Asia/Seoul']],
  ['Hong Kong', ['Asia/Hong_Kong']],
  ['Taiwan', ['Asia/Taipei']],
  ['Thailand', ['Asia/Bangkok']],
  ['Vietnam', ['Asia/Ho_Chi_Minh']],
  ['Malaysia', ['Asia/Kuala_Lumpur']],
  ['Indonesia', ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura']],
  ['China', ['Asia/Shanghai']],
  ['Qatar', ['Asia/Qatar']],
  ['Saudi Arabia', ['Asia/Riyadh']],
  ['Turkey', ['Europe/Istanbul']],
  ['France', ['Europe/Paris']],
  ['Spain', ['Europe/Madrid']],
  ['Italy', ['Europe/Rome']],
  ['Netherlands', ['Europe/Amsterdam']],
  ['Belgium', ['Europe/Brussels']],
  ['Switzerland', ['Europe/Zurich']],
  ['Austria', ['Europe/Vienna']],
  ['Sweden', ['Europe/Stockholm']],
  ['Norway', ['Europe/Oslo']],
  ['Denmark', ['Europe/Copenhagen']],
  ['Finland', ['Europe/Helsinki']],
  ['Poland', ['Europe/Warsaw']],
  ['Portugal', ['Europe/Lisbon']],
  ['Ireland', ['Europe/Dublin']],
  ['Greece', ['Europe/Athens']],
  ['Russia', ['Europe/Moscow', 'Asia/Yekaterinburg', 'Asia/Novosibirsk', 'Asia/Vladivostok']],
  ['Canada', ['America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Halifax', 'America/St_Johns']],
  ['Mexico', ['America/Mexico_City', 'America/Cancun', 'America/Tijuana']],
  ['Brazil', ['America/Sao_Paulo', 'America/Manaus']],
  ['Argentina', ['America/Argentina/Buenos_Aires']],
  ['Chile', ['America/Santiago']],
  ['Colombia', ['America/Bogota']],
  ['South Africa', ['Africa/Johannesburg']],
  ['Egypt', ['Africa/Cairo']],
  ['Nigeria', ['Africa/Lagos']],
  ['Kenya', ['Africa/Nairobi']],
  ['New Zealand', ['Pacific/Auckland']],
  ['Pakistan', ['Asia/Karachi']],
  ['Bangladesh', ['Asia/Dhaka']],
  ['Sri Lanka', ['Asia/Colombo']],
  ['Nepal', ['Asia/Kathmandu']],
  ['Israel', ['Asia/Jerusalem']],
  ['Coordinated Universal Time', ['UTC']],
];

/** `[{ country, zones: [{ id, label }] }]`, sorted by country, zones validated. */
export const TIMEZONE_CATALOG = RAW_CATALOG.map(([country, zones]) => ({
  country,
  zones: zones.filter(validateTimezone).map((id) => ({ id, label: id.replace(/_/g, ' ') })),
})).sort((a, b) => a.country.localeCompare(b.country));

/** Flat list of every zone in the catalogue (plus the browser zone if it is missing). */
export function getAllCatalogZones() {
  const ids = new Set(TIMEZONE_CATALOG.flatMap((c) => c.zones.map((z) => z.id)));
  const browser = getBrowserTimezone();
  if (validateTimezone(browser)) ids.add(browser);
  return [...ids].sort();
}

/** Country whose list contains `tz` (for pre-selecting the country dropdown). */
export function findCountryForZone(tz) {
  return TIMEZONE_CATALOG.find((c) => c.zones.some((z) => z.id === tz))?.country ?? null;
}

/** Best-effort zone for a facility from its saved timezone, else country, else business default. */
export function suggestFacilityTimezone(facility) {
  if (validateTimezone(facility?.timezone) && facility.timezone !== 'UTC') return facility.timezone;
  const country = (facility?.country ?? '').trim().toLowerCase();
  const hit = TIMEZONE_CATALOG.find((c) => c.country.toLowerCase() === country);
  if (hit?.zones?.length === 1) return hit.zones[0].id;
  return DEFAULT_BUSINESS_TIMEZONE;
}

/** 0 = Sunday … 6 = Saturday for a local calendar date. */
export function localWeekday(date) {
  return DateTime.fromISO(date, { zone: 'utc' }).weekday % 7;
}

/** Whole days from `a` to `b` (negative when `b` is earlier). */
export function daysBetween(a, b) {
  return Math.round(DateTime.fromISO(b, { zone: 'utc' }).diff(DateTime.fromISO(a, { zone: 'utc' }), 'days').days);
}
